import type { Loan } from '@/types'
import type {
  PropertyProfile,
  TenantRow,
  UnitMix,
  LeaseExpiration,
  ContactInfo,
  Comparable,
  PropertyPhoto,
} from '@/types/propertyProfile'
import {
  photosForAssetType,
  PHOTO_SET_LOGISTICS_ALT,
  PHOTO_SET_MULTIFAMILY_MIDRISE,
  PHOTO_SET_LUXURY_MF,
} from '@/data/localPropertyPhotoSets'

/** Dollars unless the book declares $MM (the curated Antarctica deals do).
 *  The unit used to be guessed from the loan's size: a $750,000 loan read as
 *  $750,000M. A size is never guessed from its magnitude (N1). */
function usesDollarScale(loan: Loan): boolean {
  return loan.amountUnit !== 'USD_MM'
}

function fmtDollarsAsM(n: number): string {
  return `$${(n / 1_000_000).toFixed(2)}M`
}

function fmtMillions(n: number): string {
  return `$${n.toFixed(2)}M`
}

function revenueAnnualDollars(loan: Loan, dollarScale: boolean): number {
  const r = loan.cashFlows.revenue
  return dollarScale ? r : r * 1_000_000
}

function expenseAnnualDollars(loan: Loan, dollarScale: boolean): number {
  const e = loan.cashFlows.expenses
  return dollarScale ? e : e * 1_000_000
}

function noiAnnualDollars(loan: Loan, dollarScale: boolean): number {
  const n = loan.noi ?? 0
  return dollarScale ? n : n * 1_000_000
}

function valueDollars(loan: Loan, dollarScale: boolean): number {
  const v = loan.propertyValue ?? 0
  return dollarScale ? v : v * 1_000_000
}

function loanAmountDisplay(loan: Loan, dollarScale: boolean): string {
  return dollarScale ? fmtDollarsAsM(loan.loanAmount) : fmtMillions(loan.loanAmount)
}

function expenseRatioDisplay(loan: Loan, dollarScale: boolean): string {
  const e = loan.cashFlows.expenseRatio
  const pct = dollarScale ? e : e < 1 ? e * 100 : e
  return `${pct.toFixed(0)}%`
}

function capRateDisplay(loan: Loan, dollarScale: boolean): string {
  const noi = noiAnnualDollars(loan, dollarScale)
  const val = valueDollars(loan, dollarScale)
  if (val <= 0 || noi <= 0) return '—'
  return `${((noi / val) * 100).toFixed(2)}%`
}

function occupancyFromMarket(loan: Loan): string {
  if (loan.type === 'Land') return '—'
  return `${Math.max(0, Math.min(100, 100 - loan.market.vacRate)).toFixed(1)}%`
}

const GEO: Record<string, { lat: number; lng: number; zip: string }> = {
  'New York, NY': { lat: 40.709, lng: -74.011, zip: '10006' },
  'Dallas, TX': { lat: 32.7767, lng: -96.797, zip: '75201' },
  'Miami, FL': { lat: 25.7617, lng: -80.1918, zip: '33131' },
  'Houston, TX': { lat: 29.7604, lng: -95.3698, zip: '77056' },
  'San Francisco, CA': { lat: 37.7749, lng: -122.4194, zip: '94102' },
  'Phoenix, AZ': { lat: 33.4484, lng: -112.074, zip: '85004' },
  'Boston, MA': { lat: 42.3601, lng: -71.0589, zip: '02110' },
  'Scottsdale, AZ': { lat: 33.4942, lng: -111.9261, zip: '85251' },
  'Chicago, IL': { lat: 41.8781, lng: -87.6298, zip: '60601' },
  'Atlanta, GA': { lat: 33.749, lng: -84.388, zip: '30309' },
  'Nashville, TN': { lat: 36.1627, lng: -86.7816, zip: '37203' },
  'Seattle, WA': { lat: 47.6062, lng: -122.3321, zip: '98101' },
  'Charlotte, NC': { lat: 35.2271, lng: -80.8431, zip: '28202' },
  'Las Vegas, NV': { lat: 36.1699, lng: -115.1398, zip: '89109' },
  'Washington, DC': { lat: 38.9072, lng: -77.0369, zip: '20004' },
  'Portland, OR': { lat: 45.5152, lng: -122.6784, zip: '97204' },
  'Denver, CO': { lat: 39.7392, lng: -104.9903, zip: '80202' },
}

function geoFor(loan: Loan) {
  return GEO[loan.location] ?? { lat: 39.8283, lng: -98.5795, zip: '00000' }
}

function pseudoParcel(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  const a = (h % 99).toString().padStart(2, '0')
  const b = ((h >> 8) % 999).toString().padStart(3, '0')
  const c = ((h >> 16) % 9999).toString().padStart(4, '0')
  return `${a}-${b}-${c}`
}

function taglineFor(loan: Loan): string {
  const y = loan.yearBuilt && loan.yearBuilt > 0 ? `${loan.yearBuilt}` : 'TBD'
  if (loan.type === 'Land') return `Entitled land | ${loan.location} | Underwriting demo`
  if (loan.units && loan.units > 0) {
    return `${loan.units.toLocaleString()}-unit ${loan.type} | Built ${y}`
  }
  const sf = loan.sf && loan.sf > 0 ? `${(loan.sf / 1000).toFixed(0)}K SF` : 'Scale per OM'
  return `${sf} ${loan.type} | Built ${y}`
}

function totalSFStr(loan: Loan): string {
  if (loan.sf && loan.sf > 0) return loan.sf.toLocaleString()
  if (loan.units && loan.units > 0 && loan.type === 'Multifamily') {
    const est = loan.units * 950
    return `${est.toLocaleString()} (est.)`
  }
  return '—'
}

function storiesFor(loan: Loan): number {
  if (loan.type === 'Industrial' || loan.type === 'Retail') return loan.yearBuilt && loan.yearBuilt < 1990 ? 2 : 1
  if (loan.type === 'Land') return 0
  if (!loan.sf || loan.sf < 1) return loan.units ? Math.min(12, 4 + Math.floor(loan.units / 80)) : 8
  if (loan.sf > 800_000) return 40
  if (loan.sf > 400_000) return 24
  if (loan.sf > 150_000) return 12
  return 6
}

function parkingFor(loan: Loan): string {
  switch (loan.type) {
    case 'Industrial':
      return 'Trailer + auto per BOMA / site plan (demo)'
    case 'Office':
      return 'Structured parking per lease grid (demo)'
    case 'Multifamily':
      return `${loan.units ? Math.round(loan.units * 1.4) : 400} spaces (1.4:1 est.)`
    case 'Retail':
      return 'Surface + shared cross-parking (demo)'
    case 'Hospitality':
      return 'On-site + adjacent structured (demo)'
    case 'Land':
      return 'Future program — TBD'
    default:
      return 'Per architectural set (demo)'
  }
}

function amenitiesFor(loan: Loan): string {
  const m: Record<string, string> = {
    Office: 'Spec suites, destination lobby, conferencing, fitness (demo)',
    Industrial: 'ESFR, cross-dock, ample power, trailer storage (demo)',
    Multifamily: 'Pool, fitness, coworking, package, pet amenities (demo)',
    Retail: 'Anchor + shop lineup, pylon signage, shared parking (demo)',
    Hospitality: 'F&B, meeting, spa-equivalent service stack (demo)',
    'Lab/R&D': 'Redundant MEP, lab gas, loading, write-up (demo)',
    'Senior Living': 'Care continuum, dining, therapy, secured memory wing (demo)',
    'Mixed-Use': 'Retail podium, residential lobby, amenity deck (demo)',
    Land: 'Entitlements in place — vertical TBD (demo)',
  }
  return m[loan.type] ?? 'See OM — representative demo copy'
}

function zoningFor(loan: Loan): string {
  const m: Record<string, string> = {
    Office: 'CBD / Central Business (demo)',
    Industrial: 'Light / Logistics Industrial (demo)',
    Multifamily: 'Multifamily Residential High Density (demo)',
    Retail: 'Commercial Retail / C-2 (demo)',
    Hospitality: 'Hospitality Overlay (demo)',
    'Lab/R&D': 'Research Campus / PUD (demo)',
    'Senior Living': 'Senior Housing Planned (demo)',
    'Mixed-Use': 'Mixed-Use Planned Development (demo)',
    Land: 'Future PD / entitlement path (demo)',
  }
  return m[loan.type] ?? 'Verify with title (demo)'
}

function photosForLoan(loan: Loan, commandCenter: boolean): PropertyPhoto[] {
  if (commandCenter) {
    if (loan.id === 'AL-2025-010') return PHOTO_SET_LOGISTICS_ALT
    if (loan.id === 'AL-2025-011') return PHOTO_SET_MULTIFAMILY_MIDRISE
    if (loan.id === 'AL-2025-014') return photosForAssetType('Multifamily')
    if (loan.id === 'AL-2025-009' || loan.id === 'AL-2025-016') return photosForAssetType('Mixed-Use')
  }
  if (loan.type === 'Multifamily' && (loan.units ?? 0) >= 280) return PHOTO_SET_LUXURY_MF
  return photosForAssetType(loan.type)
}

function rentRollToTenants(loan: Loan): TenantRow[] | undefined {
  if (!loan.rentRoll?.length) return undefined
  if (loan.type !== 'Office' && loan.type !== 'Industrial' && loan.type !== 'Lab/R&D' && loan.type !== 'Retail')
    return undefined
  return loan.rentRoll.map((r) => ({
    tenant: r.tenant,
    suite: r.suite,
    sf: r.sf,
    rentMo: r.monthlyRent,
    leaseStart: r.leaseStart || '—',
    leaseEnd: r.leaseEnd || '—',
    status: r.status,
  }))
}

function multifamilyUnitMixFromLoan(loan: Loan, dollarScale: boolean): UnitMix[] | undefined {
  if (loan.type !== 'Multifamily' || !loan.units) return undefined
  const u = loan.units
  const occ = Math.round(parseFloat(occupancyFromMarket(loan)) || 93)
  const revMo = revenueAnnualDollars(loan, dollarScale) / 12
  const studio = Math.max(1, Math.round(u * 0.15))
  const oneBr = Math.max(1, Math.round(u * 0.46))
  const threeBr = Math.max(0, Math.round(u * 0.09))
  const twoBr = Math.max(1, u - studio - oneBr - threeBr)
  const rows: { type: string; count: number; share: number }[] = [
    { type: `Studio (${studio})`, count: studio, share: 0.17 },
    { type: `1BR (${oneBr})`, count: oneBr, share: 0.42 },
    { type: `2BR (${twoBr})`, count: twoBr, share: 0.32 },
  ]
  if (threeBr > 0) rows.push({ type: `3BR (${threeBr})`, count: threeBr, share: 0.09 })
  else {
    rows[2].share += 0.09
  }
  return rows.map((r, i) => ({
    type: r.type,
    count: r.count,
    avgRent: Math.max(1, Math.round((revMo * r.share) / r.count)),
    occupancy: Math.min(98, occ + (i === 0 ? 2 : i === 3 ? -2 : 0)),
  }))
}

function effectiveSF(loan: Loan): number {
  if (loan.sf && loan.sf > 0) return loan.sf
  if (loan.units && loan.units > 0) return loan.units * 950
  return 220_000
}

function demoCommercialTenants(loan: Loan, dollarScale: boolean): TenantRow[] {
  const sfBase = effectiveSF(loan)
  const monthlyRev = Math.max(25_000, revenueAnnualDollars(loan, dollarScale) / 12)
  const vacPct = Math.min(22, Math.max(3, loan.market.vacRate))
  const occRatio = (100 - vacPct) / 100
  const occupiedTarget = Math.round(sfBase * occRatio)
  const aSf = Math.round(occupiedTarget * 0.38)
  const bSf = Math.round(occupiedTarget * 0.34)
  const cSf = Math.max(0, occupiedTarget - aSf - bSf)
  const vacantSf = Math.max(0, sfBase - aSf - bSf - cSf)
  const rows: TenantRow[] = [
    {
      tenant: 'National Credit Tenant (demo)',
      suite: 'Tower / Phase A',
      sf: aSf,
      rentMo: Math.round(monthlyRev * 0.44),
      leaseStart: '2019-06',
      leaseEnd: '2028-12',
      status: 'Current',
    },
    {
      tenant: 'Regional HQ User (demo)',
      suite: 'Mid-rise',
      sf: bSf,
      rentMo: Math.round(monthlyRev * 0.35),
      leaseStart: '2021-03',
      leaseEnd: '2027-08',
      status: 'Current',
    },
  ]
  if (cSf >= 8_000) {
    rows.push({
      tenant: 'Regional Services (demo)',
      suite: 'Podium',
      sf: cSf,
      rentMo: Math.round(monthlyRev * 0.16),
      leaseStart: '2022-01',
      leaseEnd: '2030-11',
      status: 'Current',
    })
  }
  if (vacantSf >= 4_000) {
    rows.push({
      tenant: 'Vacant / Spec',
      suite: 'Available',
      sf: vacantSf,
      rentMo: 0,
      leaseStart: '—',
      leaseEnd: '—',
      status: 'Vacant',
    })
  }
  return rows
}

function demoHospitalityTenants(loan: Loan, dollarScale: boolean): TenantRow[] {
  const sf = effectiveSF(loan)
  const monthlyRev = Math.max(120_000, revenueAnnualDollars(loan, dollarScale) / 12)
  return [
    {
      tenant: 'Franchise / Operator (demo)',
      suite: 'Full asset',
      sf: Math.round(sf * 0.86),
      rentMo: Math.round(monthlyRev * 0.78),
      leaseStart: '2016-01',
      leaseEnd: '2036-12',
      status: 'Current',
    },
    {
      tenant: 'Outparcel F&B (demo)',
      suite: 'Pad B',
      sf: Math.round(sf * 0.09),
      rentMo: Math.round(monthlyRev * 0.14),
      leaseStart: '2019-06',
      leaseEnd: '2029-05',
      status: 'Current',
    },
    {
      tenant: 'Spa / Retail (demo)',
      suite: 'Lobby',
      sf: Math.max(4_000, Math.round(sf * 0.05)),
      rentMo: Math.round(monthlyRev * 0.08),
      leaseStart: '2018-03',
      leaseEnd: '2028-02',
      status: 'Current',
    },
  ]
}

function demoSeniorLivingTenants(loan: Loan, dollarScale: boolean): TenantRow[] {
  const sf = effectiveSF(loan)
  const monthlyRev = Math.max(35_000, revenueAnnualDollars(loan, dollarScale) / 12)
  return [
    {
      tenant: 'Independent Living (demo)',
      suite: 'Wing A',
      sf: Math.round(sf * 0.41),
      rentMo: Math.round(monthlyRev * 0.36),
      leaseStart: '2017-01',
      leaseEnd: '—',
      status: 'Current',
    },
    {
      tenant: 'Assisted Living (demo)',
      suite: 'Wing B',
      sf: Math.round(sf * 0.36),
      rentMo: Math.round(monthlyRev * 0.42),
      leaseStart: '2017-01',
      leaseEnd: '—',
      status: 'Current',
    },
    {
      tenant: 'Memory Care (demo)',
      suite: 'Wing C',
      sf: Math.round(sf * 0.15),
      rentMo: Math.round(monthlyRev * 0.15),
      leaseStart: '2018-06',
      leaseEnd: '—',
      status: 'Current',
    },
    {
      tenant: 'Skilled / swing (demo)',
      suite: 'Clinical',
      sf: Math.max(6_000, Math.round(sf * 0.08)),
      rentMo: Math.round(monthlyRev * 0.07),
      leaseStart: '2019-01',
      leaseEnd: '—',
      status: 'Current',
    },
  ]
}

function demoLandTenants(loan: Loan): TenantRow[] {
  const padSf = loan.sf && loan.sf > 0 ? loan.sf : 312_000
  return [
    {
      tenant: 'Ground lease — retail pad (demo)',
      suite: 'Outparcel A',
      sf: Math.round(padSf * 0.14),
      rentMo: 42_000,
      leaseStart: '2022-01',
      leaseEnd: '2042-12',
      status: 'Current',
    },
    {
      tenant: 'Cell / easement income (demo)',
      suite: 'NE corner',
      sf: 2_400,
      rentMo: 4_800,
      leaseStart: '2014-05',
      leaseEnd: '2034-04',
      status: 'Current',
    },
    {
      tenant: 'Billboard / utilities (demo)',
      suite: 'Highway frontage',
      sf: 800,
      rentMo: 2_200,
      leaseStart: '2016-09',
      leaseEnd: '2029-08',
      status: 'Current',
    },
  ]
}

function ensureRentRollSection(
  loan: Loan,
  dollarScale: boolean,
  tenantsIn?: TenantRow[],
  unitMixIn?: UnitMix[],
): { tenants?: TenantRow[]; unitMix?: UnitMix[] } {
  if (tenantsIn?.length) return { tenants: tenantsIn }
  if (unitMixIn?.length) return { unitMix: unitMixIn }
  if (loan.type === 'Hospitality') return { tenants: demoHospitalityTenants(loan, dollarScale) }
  if (loan.type === 'Senior Living') return { tenants: demoSeniorLivingTenants(loan, dollarScale) }
  if (loan.type === 'Land') return { tenants: demoLandTenants(loan) }
  return { tenants: demoCommercialTenants(loan, dollarScale) }
}

function leaseExpirationsGeneric(loan: Loan): LeaseExpiration[] {
  const base = loan.sf && loan.sf > 0 ? loan.sf : (loan.units ?? 200) * 900
  const sf = Math.max(base, 100_000)
  return [
    { year: '2026-2027', sf: Math.round(sf * 0.28), pctOfTotal: 28 },
    { year: '2028-2029', sf: Math.round(sf * 0.27), pctOfTotal: 27 },
    { year: '2030-2031', sf: Math.round(sf * 0.25), pctOfTotal: 25 },
    { year: '2032+', sf: Math.round(sf * 0.2), pctOfTotal: 20 },
  ]
}

function contactsFor(loan: Loan): ContactInfo[] {
  const city = loan.location.split(',')[0]?.trim() ?? 'Metro'
  return [
    { role: 'Asset Lead', name: 'Jordan Ellis', company: loan.borrower, phone: '(555) 010-2200', email: `asset.team@${loan.id.toLowerCase()}.demo` },
    { role: 'Property Manager', name: 'Taylor Brooks', company: `${city} PM Group`, phone: '(555) 010-3311', email: 'pm.ops@helios.demo' },
    { role: 'Insurance', name: 'Casey Morgan', company: 'Helios Risk Partners (demo)', phone: '(555) 010-4422', email: 'cre.certs@helios.demo' },
  ]
}

function comparablesFor(loan: Loan): Comparable[] {
  const dist = ['0.6mi', '1.1mi', '1.8mi']
  return [
    { name: `${loan.market.msa.split('-')[0]} Comp A`, sf: loan.sf ? Math.round(loan.sf * 0.92) : 240000, avgRent: 32, capRate: loan.market.capRate + 0.2, distance: dist[0] },
    { name: `${loan.market.msa.split('-')[0]} Comp B`, sf: loan.sf ? Math.round(loan.sf * 1.05) : 310000, avgRent: 30, capRate: loan.market.capRate, distance: dist[1] },
    { name: `${loan.market.msa.split('-')[0]} Comp C`, sf: loan.sf ? Math.round(loan.sf * 0.88) : 195000, avgRent: 31, capRate: loan.market.capRate - 0.15, distance: dist[2] },
  ]
}

function perUnitMetricsFixed(loan: Loan, dollarScale: boolean): { rev: string; exp: string; noi: string } {
  const rev = revenueAnnualDollars(loan, dollarScale)
  const exp = expenseAnnualDollars(loan, dollarScale)
  const noi = noiAnnualDollars(loan, dollarScale)
  if (loan.type === 'Land') return { rev: '—', exp: '—', noi: '—' }
  if (loan.units && loan.units > 0) {
    const ru = rev / loan.units
    const eu = exp / loan.units
    const nu = noi / loan.units
    return {
      rev: `$${(ru / 1000).toFixed(1)}K/yr/unit`,
      exp: `$${(eu / 1000).toFixed(1)}K/yr/unit`,
      noi: `$${(nu / 1000).toFixed(1)}K/yr/unit`,
    }
  }
  const sf = loan.sf && loan.sf > 0 ? loan.sf : 1
  return {
    rev: `$${(rev / sf).toFixed(2)}/SF`,
    exp: `$${(exp / sf).toFixed(2)}/SF`,
    noi: `$${(noi / sf).toFixed(2)}/SF`,
  }
}

export function buildPropertyProfileFromLoan(loan: Loan, commandCenter: boolean): PropertyProfile {
  const $ = usesDollarScale(loan)
  const geo = geoFor(loan)
  const fullAddress = `${loan.property}, ${loan.location} ${geo.zip}`
  const pu = perUnitMetricsFixed(loan, $)
  const tenantsFromRoll = rentRollToTenants(loan)
  const unitMixMF = tenantsFromRoll?.length ? undefined : multifamilyUnitMixFromLoan(loan, $)
  const { tenants, unitMix } = ensureRentRollSection(loan, $, tenantsFromRoll, unitMixMF)

  return {
    dealId: loan.id,
    propertyName: loan.property,
    tagline: taglineFor(loan),
    location: loan.location,
    fullAddress,
    loanAmount: loanAmountDisplay(loan, $),
    product: loan.product ?? 'Senior Mortgage',
    stage: loan.stage,
    propertyType: loan.type,
    yearBuilt:
      loan.type === 'Land'
        ? 2024
        : loan.yearBuilt && loan.yearBuilt > 0
          ? loan.yearBuilt
          : 2015,
    totalUnits: loan.units,
    totalSF: totalSFStr(loan),
    avgUnitSize: loan.units && loan.sf && loan.sf > 0 ? `${Math.round(loan.sf / loan.units)} SF` : loan.units ? '~950 SF (est.)' : undefined,
    stories: storiesFor(loan),
    parking: parkingFor(loan),
    amenities: amenitiesFor(loan),
    zoning: zoningFor(loan),
    parcel: pseudoParcel(loan.id),
    lastSale: valueDollars(loan, $) > 0 ? `${fmtDollarsAsM(valueDollars(loan, $) * 1.05)} (demo est.)` : '—',
    photos: photosForLoan(loan, commandCenter),
    mapLat: geo.lat,
    mapLng: geo.lng,
    population3mi: `${(180 + (loan.id.charCodeAt(loan.id.length - 1) % 120)).toLocaleString()},000`,
    medianHHI: '$72,400',
    popGrowth: '+1.9%',
    walkScore: loan.type === 'Office' ? '88/100' : '42/100',
    crimeIndex: 'Moderate',
    empGrowth: '+2.4%',
    topEmployers: ['Fortune 500 HQ (demo)', 'Regional health system', 'Logistics employer', 'Professional services', 'Education'],
    schools: 'A-/B+ district mix (demo)',
    transit: 'Highway + transit per market (demo)',
    nearbyDevelopments: '2–4 comparable projects within 3mi (demo)',
    submarket: loan.market.msa,
    submarketVacancy: `${loan.market.vacRate.toFixed(1)}%`,
    submarketRentGrowth: `${loan.market.rentGrowth >= 0 ? '+' : ''}${loan.market.rentGrowth.toFixed(1)}%`,
    submarketAbsorption:
      loan.type === 'Multifamily'
        ? `${loan.market.absorption.toLocaleString()} units/yr`
        : loan.market.absorption >= 500_000
          ? `${(loan.market.absorption / 1_000_000).toFixed(1)}M SF/yr`
          : `${loan.market.absorption.toLocaleString()} SF/yr`,
    revenue: $ ? fmtDollarsAsM(loan.cashFlows.revenue) : fmtMillions(loan.cashFlows.revenue),
    expenses: $ ? fmtDollarsAsM(loan.cashFlows.expenses) : fmtMillions(loan.cashFlows.expenses),
    noi: $ ? fmtDollarsAsM(loan.noi ?? 0) : fmtMillions(loan.noi ?? 0),
    capRate: capRateDisplay(loan, $),
    occupancy: occupancyFromMarket(loan),
    expenseRatio: expenseRatioDisplay(loan, $),
    revPerUnit: pu.rev,
    expPerUnit: pu.exp,
    noiPerUnit: pu.noi,
    unitMix,
    tenants,
    avgRentUnit: loan.units ? `$${Math.round(revenueAnnualDollars(loan, $) / loan.units / 12).toLocaleString()}/mo` : undefined,
    avgRentSF: (() => {
      const r = revenueAnnualDollars(loan, $)
      if (loan.sf && loan.sf > 0) return `$${(r / loan.sf).toFixed(2)}/SF`
      if (loan.units && loan.type === 'Multifamily') return `$${(r / (loan.units * 950)).toFixed(2)}/SF (est.)`
      if (loan.type !== 'Land' && r > 0) return `$${(r / effectiveSF(loan)).toFixed(2)}/SF (demo)`
      return undefined
    })(),
    leaseRenewalRate: '68%',
    avgLeaseTerm: '11 months',
    concessions: 'Market concessions per T-12 (demo)',
    leaseExpirations: leaseExpirationsGeneric(loan),
    contacts: contactsFor(loan),
    comparables: comparablesFor(loan),
    compSummary: 'Demo comp set synthesized from submarket stats — illustrative only.',
    lastInspection: 'Jan–Mar 2026',
    inspector: 'Helios Demo Engineering',
    overallCondition: 'Good to Excellent (demo)',
    immediateRepairs: '$125,000 (demo)',
    deferredMaintenance: '$420,000 over 5 yrs (demo)',
    environmental: 'Phase I ESA — no RECs identified (demo)',
    seismicZone: 'Per local code (demo)',
    floodZone: 'X / AE per preliminary maps (demo)',
    sponsorId: 'helios-demo-sponsor',
  }
}
